/**
 * Leave Requests page — retrofitted in R2 to use the locked
 * design-system primitives.
 *
 * Layout:
 *   - <PageHeader />: title + breadcrumbs (Operations > Leave >
 *     Requests) + "Request leave" primary action.
 *   - <DataTable />: filterable list with sort, search, columns,
 *     Excel + CSV export, row-click → EntityDrawer.
 *   - <EntityDrawer />: detail view with state badge, balance
 *     snapshot, denial reason, workflow_event history timeline.
 *   - New Request modal kept (≤5 fields per §7 component rules).
 *
 * AI marker convention: rows whose `reason` looks like it came
 * from the Concierge (free-text natural language) get an <AIBadge />
 * + <AutonomyBadge outcome="drafted" /> for transparency. A
 * dedicated `ai_handled` API flag will replace this heuristic in a
 * future PR.
 */

import type { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AIBadge,
  AutonomyBadge,
  DataTable,
  EmptyState,
  EntityDrawer,
  OperationsReportsButton,
  PageHeader,
  TraceableValue,
  WorkflowStateBadge,
} from "../../../components/ui";
import { HrInitiateLink } from "../../../components/hr_operations";
import { ErrorPanel, Loading } from "../../../components/Loading";
import { api, ApiError } from "../../../lib/api";
import type { LeaveRequestDetail, LeaveTypeDetail } from "../../../lib/api";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { useCurrentEmployeeId } from "../../_session3dShell";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// Heuristic: a Concierge-created request has a multi-word natural-
// language reason. Server-side `ai_handled` flag would be more
// reliable — backlog item.
function isAIHandled(req: LeaveRequestDetail): boolean {
  if (!req.reason) return false;
  return /\b(want|need|take|book|أريد|طلب)\b/i.test(req.reason);
}

// ---------------------------------------------------------------------------
// New Request Modal — ≤5 fields, modal is correct per §7
// ---------------------------------------------------------------------------

function NewLeaveRequestDialog({
  employeeId,
  legalEntityId,
  leaveTypes,
  onClose,
  onCreated,
}: {
  employeeId: string;
  legalEntityId: string;
  leaveTypes: LeaveTypeDetail[];
  onClose: () => void;
  onCreated: (req: LeaveRequestDetail) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    leave_type_id: leaveTypes[0]?.id ?? "",
    start_date: "",
    end_date: "",
    reason: "",
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const computedDays = useMemo(() => {
    if (!form.start_date || !form.end_date) return null;
    const start = new Date(form.start_date);
    const end = new Date(form.end_date);
    const ms = end.getTime() - start.getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    return Math.floor(ms / (1000 * 60 * 60 * 24)) + 1;
  }, [form.start_date, form.end_date]);

  const mutation = useMutation({
    mutationFn: () =>
      api.createLeaveRequest({
        employee_id: employeeId,
        legal_entity_id: legalEntityId,
        leave_type_id: form.leave_type_id,
        start_date: form.start_date,
        end_date: form.end_date,
        days_requested: computedDays?.toString() ?? "0",
        reason: form.reason || null,
      }),
    onSuccess: (req) => onCreated(req),
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.detail : String(err));
    },
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("operations.leave_request_cta")}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-sm border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="new-leave-form"
            disabled={mutation.isPending}
            className="rounded-sm bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending
              ? t("operations.leave_submitting")
              : t("operations.leave_request_submit")}
          </button>
        </div>
      }
    >
      <form
        id="new-leave-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErrorMsg(null);
          mutation.mutate();
        }}
        className="flex flex-col gap-3 text-sm"
      >
          <label className="block">
            <span className="text-xs font-medium uppercase text-text-tertiary">
              {t("operations.leave_type_label")}
            </span>
            <select
              value={form.leave_type_id}
              onChange={(e) =>
                setForm({ ...form, leave_type_id: e.target.value })
              }
              className="mt-1 w-full rounded-sm border border-border bg-surface-default px-3 py-2"
              required
            >
              {leaveTypes.map((lt) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name_en} ({lt.code})
                </option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium uppercase text-text-tertiary">
                {t("operations.leave_start_date")}
              </span>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) =>
                  setForm({ ...form, start_date: e.target.value })
                }
                className="mt-1 w-full rounded-sm border border-border bg-surface-default px-3 py-2"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase text-text-tertiary">
                {t("operations.leave_end_date")}
              </span>
              <input
                type="date"
                value={form.end_date}
                onChange={(e) =>
                  setForm({ ...form, end_date: e.target.value })
                }
                className="mt-1 w-full rounded-sm border border-border bg-surface-default px-3 py-2"
                required
              />
            </label>
          </div>
          {computedDays !== null && (
            <p className="text-xs text-text-secondary">
              <span className="font-medium">{t("operations.leave_days_requested")}</span>{" "}
              <span data-testid="leave-days-computed">
                {computedDays} {computedDays === 1 ? t("common.day", "day") : t("common.days", "days")}
              </span>
            </p>
          )}
          <label className="block">
            <span className="text-xs font-medium uppercase text-text-tertiary">
              {t("operations.leave_reason_optional")}
            </span>
            <textarea
              value={form.reason}
              onChange={(e) => setForm({ ...form, reason: e.target.value })}
              rows={3}
              className="mt-1 w-full rounded-sm border border-border bg-surface-default px-3 py-2"
            />
          </label>
        {errorMsg && (
          <p className="rounded-sm bg-state-denied-bg p-2 text-state-denied">
            {errorMsg}
          </p>
        )}
      </form>
    </EntityDrawer>
  );
}

// ---------------------------------------------------------------------------
// Detail Drawer — history timeline
// ---------------------------------------------------------------------------

function RequestDetailBody({ requestId }: { requestId: string }) {
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ["leave-request-history", requestId],
    queryFn: () => api.getLeaveRequestHistory(requestId),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorPanel message={(q.error as Error).message} />;
  const events = q.data ?? [];
  if (events.length === 0) {
    return (
      <p className="text-sm italic text-text-tertiary">
        {t("operations.leave_no_events")}{" "}
        <span className="not-italic">
          {t(
            "operations.leave_no_events_hint",
            "— workflow events appear after the request is submitted.",
          )}
        </span>
      </p>
    );
  }
  return (
    <ol className="flex flex-col gap-2">
      {events.map((e) => (
        <li
          key={e.id}
          className="flex gap-3 rounded-sm border border-border-subtle p-2 text-xs"
        >
          <div className="font-mono text-text-tertiary tabular-nums">
            {new Date(e.occurred_at).toLocaleString()}
          </div>
          <div className="flex-1">
            <span className="font-semibold">{e.transition}</span>
            {e.from_state && (
              <span className="ms-2 text-text-secondary">
                {e.from_state} → {e.to_state}
              </span>
            )}
            {e.reason && (
              <span className="ms-2 text-text-primary">— {e.reason}</span>
            )}
            {e.actor_clerk_user_id && (
              <span className="ms-2 text-text-tertiary">
                by {e.actor_clerk_user_id}
              </span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function LeaveRequestsPage() {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  // M3X.LIST-AND-REPORT-FIXES PR 1: employeeId is OPTIONAL. When absent
  // the query fires against the tenant-wide list. When present, the
  // backend filters to that single employee. Either way the empty
  // state only renders if the query returns 0 rows — never on cold load.
  const employeeId = params.get("employee_id") || undefined;
  // Self-service: when no `?employee_id=` URL param is present, fall
  // back to the current logged-in user's employee id (resolved via
  // `/api/me`). This is what lets the M15 UI_LEAVE_001 Playwright
  // scenario click "New Leave Request" directly — the user is acting
  // on themselves, not on behalf of someone else.
  const selfEmployeeId = useCurrentEmployeeId();
  const newRequestTargetId = employeeId ?? (selfEmployeeId || undefined);
  const { legalEntityId: le, gate } = useLegalEntityGate();

  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState<LeaveRequestDetail | null>(null);
  const [traceRow, setTraceRow] = useState<LeaveRequestDetail | null>(null);
  const qc = useQueryClient();

  const listQ = useQuery({
    queryKey: ["leave-requests", employeeId ?? "all"],
    queryFn: () => api.listLeaveRequests({ employeeId }),
  });

  const leaveTypesQ = useQuery({
    queryKey: ["leave-types", le],
    queryFn: () => api.listLeaveTypes(le),
    enabled: !!le,
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) =>
      api.cancelLeaveRequest(id, { actor: "self" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leave-requests"] });
      qc.invalidateQueries({ queryKey: ["leave-request-history"] });
      setSelected(null);
    },
  });

  // Column definitions — built once.
  const columns = useMemo<ColumnDef<LeaveRequestDetail>[]>(() => {
    const types = leaveTypesQ.data ?? [];
    return [
      {
        id: "employee",
        accessorFn: (row) =>
          row.employee_name_en ?? row.employee_id,
        header: t("operations.leave_col_employee"),
        cell: ({ row }) => {
          // M3X.LIST-AND-REPORT-FIXES PR 2 — render pre-joined employee
          // name. Falls back to UUID prefix only when the join misses.
          const isAr =
            typeof window !== "undefined" &&
            window.document?.documentElement?.dir === "rtl";
          const name =
            (isAr && row.original.employee_name_ar) ||
            row.original.employee_name_en ||
            row.original.employee_id.slice(0, 8);
          const code = row.original.employee_code;
          return (
            <span className="inline-flex items-center gap-1.5">
              {isAIHandled(row.original) && (
                <AIBadge tooltipText={t("operations.leave_submitted_via_concierge")} />
              )}
              <span className="text-sm">{name}</span>
              {code && (
                <span className="font-mono text-xs text-text-tertiary">
                  · {code}
                </span>
              )}
            </span>
          );
        },
      },
      {
        id: "leave_type",
        accessorFn: (row) =>
          // M3X.LIST-AND-REPORT-FIXES PR 2 — prefer pre-joined name;
          // fall back to types lookup if pre-join missed.
          row.leave_type_name_en ??
          types.find((tp) => tp.id === row.leave_type_id)?.name_en ??
          row.leave_type_id,
        header: t("operations.leave_type_label"),
      },
      {
        id: "dates",
        accessorFn: (row) => row.start_date,
        header: t("operations.leave_col_dates"),
        cell: ({ row }) => (
          <span className="font-mono tabular-nums text-xs">
            {row.original.start_date} → {row.original.end_date}
          </span>
        ),
      },
      {
        id: "days",
        accessorKey: "days_requested",
        header: t("operations.leave_col_days"),
        cell: ({ row }) => (
          <TraceableValue
            onTraceClick={() => setTraceRow(row.original)}
          >
            {row.original.days_requested}
          </TraceableValue>
        ),
      },
      {
        id: "state",
        accessorKey: "workflow_state",
        header: t("operations.leave_col_state"),
        cell: ({ row }) => (
          <WorkflowStateBadge state={row.original.workflow_state} />
        ),
      },
      {
        // The reason is the human-readable handle for each request,
        // surfaced so list filters / scenario `tr:has-text(...)`
        // matches can find a row by the user-typed reason. Hidden
        // gracefully when empty.
        id: "reason",
        accessorKey: "reason",
        header: t("leave.reason_optional", "Reason"),
        cell: ({ row }) => (
          <span className="line-clamp-1 text-xs text-text-secondary">
            {row.original.reason ?? ""}
          </span>
        ),
      },
      {
        id: "outcome",
        accessorFn: (row) =>
          isAIHandled(row)
            ? row.workflow_state === "approved"
              ? "auto"
              : "drafted"
            : "",
        header: t("operations.leave_ai_outcome"),
        cell: ({ row }) => {
          if (!isAIHandled(row.original)) {
            return (
              <span className="text-xs text-text-tertiary">—</span>
            );
          }
          const outcome =
            row.original.workflow_state === "approved" ? "auto" : "drafted";
          return <AutonomyBadge outcome={outcome} />;
        },
      },
    ];
  }, [leaveTypesQ.data, t]);

  if (gate) return gate;
  if (listQ.isLoading) return <Loading />;
  if (listQ.error)
    return <ErrorPanel message={(listQ.error as Error).message} />;

  const requests = listQ.data ?? [];
  const leaveTypes = leaveTypesQ.data ?? [];

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("leave.requests_title")}
        breadcrumbs={[
          { label: t("nav.operations"), href: "/operations" },
          { label: t("nav.leave"), href: "/operations/leave/requests" },
          { label: t("leave.requests_breadcrumb") },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <HrInitiateLink transactionType="leave" />
            <OperationsReportsButton
              domain="leaves"
              domainLabel={t("nav.leave", "Leaves")}
            />
            {newRequestTargetId && (
              <button
                type="button"
                onClick={() => setShowNew(true)}
                disabled={leaveTypes.length === 0}
                className="flex items-center gap-1 rounded-sm bg-brand px-3 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
                aria-label={t("leave.new_request", "New Leave Request")}
              >
                <Plus size={14} /> {t("leave.new_request")}
              </button>
            )}
          </div>
        }
      />

      <DataTable<LeaveRequestDetail>
        columns={columns}
        data={requests}
        onRowClick={(row) => setSelected(row)}
        exportFilename="leave-requests"
        emptyState={
          <EmptyState
            headline={t(
              "operations.leave_empty_headline",
              "No leave requests yet",
            )}
            description={t(
              "operations.leave_empty_body",
              'Click "Create on behalf" to log a leave request for any employee.',
            )}
          />
        }
      />

      {/* Detail drawer */}
      <EntityDrawer
        open={selected !== null}
        onOpenChange={(open) => !open && setSelected(null)}
        title={
          selected
            ? t("operations.leave_request_id_title", {
                id: selected.id.slice(0, 8),
              })
            : t("operations.leave_request_label")
        }
        description={
          selected
            ? t("operations.leave_drawer_subtitle", {
                start: selected.start_date,
                end: selected.end_date,
                days: selected.days_requested,
              })
            : undefined
        }
        footer={
          selected &&
          ["draft", "submitted", "pending_approval"].includes(
            selected.workflow_state,
          ) ? (
            <button
              type="button"
              onClick={() => cancelMutation.mutate(selected.id)}
              disabled={cancelMutation.isPending}
              className="rounded-sm border border-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken disabled:opacity-50"
            >
              {cancelMutation.isPending
                ? t("operations.leave_cancelling")
                : t("operations.leave_cancel_request")}
            </button>
          ) : undefined
        }
      >
        {selected && (
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-2">
              <WorkflowStateBadge state={selected.workflow_state} />
              {isAIHandled(selected) && (
                <AutonomyBadge
                  outcome={
                    selected.workflow_state === "approved"
                      ? "auto"
                      : "drafted"
                  }
                />
              )}
            </div>
            {selected.balance_snapshot_at_submit && (
              <p className="text-sm text-text-secondary">
                {t("operations.leave_balance_at_submit")}{" "}
                <TraceableValue
                  onTraceClick={() => setTraceRow(selected)}
                >
                  {selected.balance_snapshot_at_submit} {t("operations.leave_days_unit")}
                </TraceableValue>
              </p>
            )}
            {selected.denial_reason && (
              <div className="rounded-sm bg-state-denied-bg p-2 text-xs text-state-denied">
                {t("operations.leave_denial_prefix")} {selected.denial_reason}
              </div>
            )}
            <div className="border-t border-border-subtle pt-3">
              <a
                href={`/operations/leaves/${selected.id}`}
                className="inline-block rounded border border-brand-blue px-3 py-1.5 text-xs font-medium text-brand-blue hover:bg-brand-blue hover:text-white"
              >
                {t("operations.leave_open_rich_detail")}
              </a>
            </div>
            <div className="border-t border-border-subtle pt-3">
              <h3 className="mb-2 text-xs font-medium uppercase text-text-tertiary">
                {t("operations.leave_history_label")}
              </h3>
              <RequestDetailBody requestId={selected.id} />
            </div>
          </div>
        )}
      </EntityDrawer>

      {/* Trace drawer — placeholder for now; opens the same drawer with
          a "trace coming in M19 ingestion" message. R5 polishes. */}
      <EntityDrawer
        open={traceRow !== null}
        onOpenChange={(open) => !open && setTraceRow(null)}
        title={t("operations.leave_calculation_trace")}
        description={
          traceRow
            ? t("operations.leave_calc_trace_subtitle", {
                days: traceRow.days_requested,
              })
            : undefined
        }
        width="lg"
      >
        <p className="text-sm text-text-secondary">
          {t("operations.leave_calc_trace_body")}
        </p>
      </EntityDrawer>

      {/* New Request modal. Available when an employee is in scope
          either via the URL (`?employee_id=...` — the "on behalf of"
          path) OR via the current logged-in user (the self-service
          path used by Playwright scenarios). The tenant-wide
          HR-initiated flow lives separately on the HrInitiateLink
          button. */}
      {showNew && newRequestTargetId && (
        <NewLeaveRequestDialog
          employeeId={newRequestTargetId}
          legalEntityId={le}
          leaveTypes={leaveTypes}
          onClose={() => setShowNew(false)}
          onCreated={(req) => {
            setShowNew(false);
            qc.invalidateQueries({ queryKey: ["leave-requests"] });
            setSelected(req);
          }}
        />
      )}
    </div>
  );
}
